import {Nullable} from '@wandb/weave/common/util/types';
import {RunsTableRun} from 'src/components/RunsTable';

import {ReportsTableReport} from '../../../components/ReportsTable';
import {imgSrcWithDefault} from '../../../util/images';
import {project, reportView} from '../../../util/urls';

export enum HomeDataType {
  Runs = 'Runs',
  Projects = 'Projects',
  Reports = 'Reports',
}

export type AccountProjectData = {
  id: string;
  name: string;
  entityName: string;
  createdAt: string;
  access?: Nullable<string>;
  user?: Nullable<{username?: Nullable<string>}>;
  totalRuns?: Nullable<number>;
  lastActive?: Nullable<string>;
  description?: Nullable<string>;
};

export type HomePageProject = {
  id: string;
  url: string;
  name: string;
  runCount: number;
  createdAt: string;
  lastActive: string;
  description: string;
  dataType: HomeDataType;
  timestamp: number;
  entityName: string;
  userName: string;
};

export function getHomePageProject(p: AccountProjectData): HomePageProject {
  const lastActive = p.lastActive ?? p.createdAt;
  return {
    timestamp: new Date(lastActive).getTime(),
    dataType: HomeDataType.Projects,
    id: p.id,
    url: project({name: p.name, entityName: p.entityName}),
    name: p.name,
    runCount: p.totalRuns ?? 0,
    createdAt: p.createdAt,
    lastActive,
    description: p.description ?? '',
    entityName: p.entityName,
    userName: p.user?.username ?? '',
  };
}

export type HomePageReport = {
  id: string;
  url: string;
  name: string;
  description: string;
  imageURL: string;
  lastEdit: string;
  dataType: HomeDataType;
  timestamp: number;
  entityName: string;
  userName: string;
  projectName: string;
  viewCount: number;
};

export function getHomePageReport(
  r: ReportsTableReport
): HomePageReport | null {
  const entityName = r.project?.entityName;
  const projectName = `project` in r ? r.project?.name : undefined;
  if (!entityName || !r.displayName) {
    return null;
  }
  return {
    timestamp: new Date(r.updatedAt).getTime(),
    dataType: HomeDataType.Reports,
    id: r.id,
    url: reportView({
      entityName,
      projectName,
      reportID: r.id,
      reportName: r.displayName,
    }),
    name: r.displayName,
    description: r.description ?? ``,
    imageURL: imgSrcWithDefault(r.previewUrl),
    lastEdit: r.updatedAt,
    entityName,
    userName: r.user?.username ?? '',
    projectName: r.project.name,
    viewCount: r.viewCount ?? 0,
  };
}

export type HomePageRun = {
  timestamp: number;
  id: string;
  name: string;
  state: string;
  updatedAt: string;
  userName: string;
  projectName: string;
  entityName: string;
  url: string;
  dataType: HomeDataType;
};

export function getHomePageRun(r: RunsTableRun): HomePageRun {
  return {
    dataType: HomeDataType.Runs,
    timestamp: new Date(r.updatedAt).getTime(),
    id: r.id,
    name: r.displayName,
    state: r.state,
    updatedAt: r.updatedAt,
    userName: r.user.username,
    projectName: r.project.name,
    entityName: r.project.entityName,
    url: `/${r.project.entityName}/${r.project.name}/runs/${r.name}`,
  };
}
